/**
 * IconButton — small wrapper around shadcn <Button size="icon"> that
 * automatically attaches a Tooltip with the supplied `label`. Use this
 * for icon-only actions in tables and toolbars so the affordance is
 * discoverable without sacrificing density.
 *
 * Passes through any extra props (onClick, variant, data-testid, ...).
 *
 * Usage:
 *   <IconButton label="View invoice" testid="invoice-view-123" onClick={...}>
 *     <Eye className="h-3 w-3" />
 *   </IconButton>
 */
import React from 'react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip';

export const IconButton = React.forwardRef(function IconButton(
  { label, children, testid, variant = 'ghost', size = 'sm', side = 'top', className = '', ...rest },
  ref
) {
  const btn = (
    <Button
      ref={ref}
      variant={variant}
      size={size}
      aria-label={label}
      title={label}
      data-testid={testid}
      className={className}
      {...rest}
    >
      {children}
    </Button>
  );
  if (!label) return btn;
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>{btn}</TooltipTrigger>
        <TooltipContent side={side} className="text-xs">{label}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
});

export default IconButton;
